import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Archive, AlertTriangle, X, Search, Building2, ArrowRight } from 'lucide-react';
import apiClient from '../api/client';

/**
 * Delete Vendor Modal
 * - No invoices → can hard delete or archive (soft delete)
 * - Has invoices → can reassign invoices to another vendor then hard delete, or archive
 */
export default function DeleteVendorModal({ vendor, onConfirm, onClose }) {
  const [deleteType, setDeleteType] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Reassignment state
  const [allVendors, setAllVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedTargetVendor, setSelectedTargetVendor] = useState(null);
  const [vendorSearch, setVendorSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [loadingVendors, setLoadingVendors] = useState(false);

  const vendorName = vendor?.VendorName || 'Unknown';
  const invoiceCount = vendor?.InvoiceCount || 0;
  const hasInvoices = invoiceCount > 0;

  // Load available vendors and departments when user picks "hard" and vendor has invoices
  useEffect(() => {
    if (deleteType === 'hard' && hasInvoices) {
      loadVendorsAndDepartments();
    }
  }, [deleteType]);

  const loadVendorsAndDepartments = async () => {
    setLoadingVendors(true);
    try {
      const [vendorRes, deptRes] = await Promise.all([
        apiClient.get('/vendors'),
        apiClient.get('/departments')
      ]);
      const vendors = (vendorRes.data.vendors || vendorRes.data || [])
        .filter(v => v.VendorID !== vendor.VendorID && v.IsActive);
      setAllVendors(vendors);
      setDepartments(deptRes.data.departments || deptRes.data || []);
    } catch (err) {
      console.error('Failed to load vendors/departments:', err);
    } finally {
      setLoadingVendors(false);
    }
  };

  // Filter vendors by search and department
  const filteredVendors = useMemo(() => {
    let list = allVendors;
    if (vendorSearch.trim()) {
      const q = vendorSearch.toLowerCase();
      list = list.filter(v =>
        v.VendorName?.toLowerCase().includes(q) ||
        v.ContactEmail?.toLowerCase().includes(q)
      );
    }
    // Department filtering would need vendor-department data; for now filter by name/email
    return list;
  }, [allVendors, vendorSearch, departmentFilter]);

  const handleConfirm = async () => {
    if (!deleteType) return;
    if (deleteType === 'hard' && hasInvoices && !selectedTargetVendor) return;
    setConfirming(true);
    try {
      await onConfirm(deleteType, selectedTargetVendor?.VendorID || null);
    } catch {
      setConfirming(false);
    }
  };

  // Check if confirm button should be enabled
  const canConfirm = deleteType === 'soft' ||
    (deleteType === 'hard' && !hasInvoices) ||
    (deleteType === 'hard' && hasInvoices && selectedTargetVendor);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-red-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Vendor</h3>
              <p className="text-sm text-gray-500">{vendorName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-red-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600">
            Choose how you want to delete vendor <strong>{vendorName}</strong>
            {hasInvoices && (
              <span> ({invoiceCount} invoice{invoiceCount !== 1 ? 's' : ''} linked)</span>
            )}:
          </p>

          {/* Option: Hard Delete */}
          <button
            onClick={() => { setDeleteType('hard'); setSelectedTargetVendor(null); }}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
              deleteType === 'hard'
                ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
            }`}
          >
            <div className="flex items-start gap-3">
              <Trash2 className={`w-5 h-5 mt-0.5 ${deleteType === 'hard' ? 'text-red-600' : 'text-gray-400'}`} />
              <div>
                <p className="font-semibold text-gray-900">Permanent Delete</p>
                <p className="text-sm text-gray-500 mt-1">
                  {hasInvoices
                    ? <>Reassign {invoiceCount} invoice{invoiceCount !== 1 ? 's' : ''} to another vendor, then permanently delete this vendor.</>
                    : <>Permanently removes the vendor and all associated data. <span className="text-red-600 font-medium">This cannot be undone.</span></>
                  }
                </p>
              </div>
            </div>
          </button>

          {/* Reassignment picker — shown only when hard delete + has invoices */}
          {deleteType === 'hard' && hasInvoices && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50/30 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <ArrowRight className="w-4 h-4 text-red-500" />
                Reassign {invoiceCount} invoice{invoiceCount !== 1 ? 's' : ''} to:
              </div>

              {/* Search + Department filter */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={vendorSearch}
                    onChange={e => setVendorSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400"
                  />
                </div>
                <select
                  value={departmentFilter}
                  onChange={e => setDepartmentFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-red-300 focus:border-red-400"
                >
                  <option value="">All Departments</option>
                  {departments.map(d => (
                    <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName}</option>
                  ))}
                </select>
              </div>

              {/* Vendor list */}
              {loadingVendors ? (
                <div className="text-center py-4 text-sm text-gray-500">Loading vendors...</div>
              ) : filteredVendors.length === 0 ? (
                <div className="text-center py-4 text-sm text-gray-500">No vendors found</div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
                  {filteredVendors.map(v => (
                    <button
                      key={v.VendorID}
                      onClick={() => setSelectedTargetVendor(v)}
                      className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors ${
                        selectedTargetVendor?.VendorID === v.VendorID
                          ? 'bg-blue-50 border-l-4 border-l-blue-500'
                          : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{v.VendorName}</p>
                        {v.ContactEmail && (
                          <p className="text-xs text-gray-500">{v.ContactEmail}</p>
                        )}
                      </div>
                      {selectedTargetVendor?.VendorID === v.VendorID && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Selected</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Selected target summary */}
              {selectedTargetVendor && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-800">
                    {invoiceCount} invoice{invoiceCount !== 1 ? 's' : ''} will be moved to <strong>{selectedTargetVendor.VendorName}</strong> before deletion.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Option: Soft Delete (Archive) */}
          <button
            onClick={() => { setDeleteType('soft'); setSelectedTargetVendor(null); }}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
              deleteType === 'soft'
                ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
            }`}
          >
            <div className="flex items-start gap-3">
              <Archive className={`w-5 h-5 mt-0.5 ${deleteType === 'soft' ? 'text-amber-600' : 'text-gray-400'}`} />
              <div>
                <p className="font-semibold text-gray-900">Archive (Soft Delete)</p>
                <p className="text-sm text-gray-500 mt-1">
                  Sets the vendor to inactive. Invoice history is preserved and the vendor can be reactivated later.
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={confirming}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || confirming}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
              !canConfirm || confirming
                ? 'bg-gray-300 cursor-not-allowed'
                : deleteType === 'hard'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {confirming ? (
              <>
                <span className="animate-spin">⏳</span>
                {deleteType === 'hard' && hasInvoices ? 'Reassigning & Deleting...' : 'Deleting...'}
              </>
            ) : deleteType === 'hard' ? (
              <>
                <Trash2 className="w-4 h-4" />
                {hasInvoices ? 'Reassign & Delete' : 'Delete Permanently'}
              </>
            ) : deleteType === 'soft' ? (
              <>
                <Archive className="w-4 h-4" />
                Archive Vendor
              </>
            ) : (
              'Select an option'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
